import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineLightBulb,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService } from '../../../services';
import masterDataService from '../../../services/masterDataService';
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import "../PlannedMaintenanceMasters.css";

// Synchronous cache loading function
const getCachedTechnicianSkillsData = () => {
  try {
    const cachedSkills = sessionStorage.getItem('technician_skills_data');
    
    return {
      skills: cachedSkills ? JSON.parse(cachedSkills) : [],
      hasAll: !!cachedSkills
    };
  } catch (e) {
    console.error('Error parsing cached Technician Skills data:', e);
    return { skills: [], hasAll: false };
  }
};

const TechnicianSkillsPage = () => {
  // Initialize state with cached data if available (synchronous)
  const cachedInitialData = getCachedTechnicianSkillsData();
  const [skills, setSkills] = useState(cachedInitialData.skills);
  const [loading, setLoading] = useState(!cachedInitialData.hasAll);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [formData, setFormData] = useState({
    skillName: '',
    description: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      // Load fresh data in background if we have cached data, otherwise show loading
      const hasCachedData = cachedInitialData.hasAll;
      loadSkills(!hasCachedData);
    } else {
      setError('Please login to view technician skills');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const loadSkills = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError('');
      const data = await masterDataService.getTechnicianSkills(tenantId);
      setSkills(data || []);
      
      // Cache the data
      sessionStorage.setItem('technician_skills_data', JSON.stringify(data || []));
    } catch (err) {
      setError('Failed to load technician skills. Please try again.');
      console.error('Error loading technician skills:', err);
      // Don't clear cached data on error, keep showing it
      if (!cachedInitialData.hasAll) {
        setSkills([]);
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredSkills = skills.filter((skill) =>
    skill.skillName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    skill.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingSkill(null);
    setFormData({
      skillName: '',
      description: '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (skill) => {
    setEditingSkill(skill);
    setFormData({
      skillName: skill.skillName || '',
      description: skill.description || '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await masterDataService.deleteTechnicianSkill(id);
      await loadSkills();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete technician skill. Please try again.');
      console.error('Error deleting technician skill:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.skillName.trim()) {
      errors.skillName = 'Skill name is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const skillData = {
        tenantId: tenantId,
        skillName: formData.skillName.trim(),
        description: formData.description.trim() || null,
      };

      if (editingSkill) {
        skillData.skillId = editingSkill.skillId;
        await masterDataService.updateTechnicianSkill(editingSkill.skillId, skillData);
      } else {
        await masterDataService.createTechnicianSkill(skillData);
      }

      await loadSkills();
      setShowModal(false);
      setFormData({ skillName: '', description: '' });
      setEditingSkill(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save technician skill. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSkill(null);
    setFormData({ skillName: '', description: '' });
    setFormErrors({});
  };

  if (loading) {
    return (
      <div className="pm-masters-page">
        <PageLoading message="Loading technician skills..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Technician Skills</h1>
          <p>Manage technician skills for Planned Maintenance</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus />
          Add Skill
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search skills..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredSkills.length} {filteredSkills.length === 1 ? 'skill' : 'skills'}
          </div>
        </div>

        {filteredSkills.length === 0 ? (
          <div className="empty-state">
            <HiOutlineLightBulb className="empty-icon" />
            <h3>No skills found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first technician skill'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Skill
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Skill Name</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSkills.map((skill) => (
                <tr key={skill.skillId}>
                  <td>{skill.skillId}</td>
                  <td className="name-cell">{skill.skillName}</td>
                  <td>{skill.description || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(skill)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(skill)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingSkill ? 'Edit Technician Skill' : 'Add New Technician Skill'}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="technician-skill-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingSkill
                  ? 'Updating...'
                  : 'Creating...'
                : editingSkill
                ? 'Update'
                : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="technician-skill-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="skillName">
                Skill Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="skillName"
                value={formData.skillName}
                onChange={(e) =>
                  setFormData({ ...formData, skillName: e.target.value })
                }
                className={formErrors.skillName ? 'error' : ''}
                placeholder="Enter skill name"
              />
              {formErrors.skillName && (
                <span className="field-error">{formErrors.skillName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Enter description (optional)"
                rows="4"
              />
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="small"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm.skillId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{' '}
            <strong>{deleteConfirm?.skillName}</strong>?
          </p>
          <p className="warning-text">
            This action cannot be undone.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default TechnicianSkillsPage;
